import { useEffect, useRef, useState } from "react";
import { Eye, EyeOff } from "lucide-react";
import { z, zodResolver } from "@pos/shared";
import {
  Button,
  Input,
  Field,
  FieldLabel,
  FieldError,
  FieldGroup,
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupInput,
  useForm,
  Controller,
} from "@pos/ui";

interface LoginFormProps {
  onSubmit: (userName: string, password: string) => void;
  isLoading: boolean;
  error: string | null;
  deviceConflictError?: string | null;
  onForceLogin?: (() => void) | null;
  onChangeDevice: () => void;
  // Persisted store display name from a prior login. When present we
  // greet the user with the store name; otherwise we show a generic
  // "Welcome back".
  storeLabel?: string | null;
}

const loginSchema = z.object({
  userName: z.string().min(1, "Username is required"),
  password: z.string().min(1, "Password is required"),
});

type LoginFormValues = z.infer<typeof loginSchema>;

// In production the "Change Store" link is hidden — operators
// shouldn't be able to swap shop config. To unlock it for the
// management team, enter the Konami code on the login screen
// (↑ ↑ ↓ ↓ ← → ← → B A). Dev/staging keep the link visible.
const APP_ENV = (import.meta.env.VITE_APP_ENV ?? "development").toLowerCase();
const IS_PRODUCTION = APP_ENV === "production";
const KONAMI_SEQUENCE = [
  "ArrowUp",
  "ArrowUp",
  "ArrowDown",
  "ArrowDown",
  "ArrowLeft",
  "ArrowRight",
  "ArrowLeft",
  "ArrowRight",
  "b",
  "a",
] as const;

export function LoginForm({
  onSubmit,
  isLoading,
  error,
  deviceConflictError,
  onForceLogin,
  onChangeDevice,
  storeLabel,
}: LoginFormProps) {
  const [showPassword, setShowPassword] = useState(false);

  // Konami unlock for the production-only "Change Store" link. Dev/
  // staging start unlocked so the link is always visible there.
  const [changeStoreUnlocked, setChangeStoreUnlocked] =
    useState(!IS_PRODUCTION);
  const konamiBufferRef = useRef<string[]>([]);

  useEffect(() => {
    if (changeStoreUnlocked) return;

    const handler = (e: KeyboardEvent) => {
      // Normalize letter keys to lowercase so caps/shift don't break
      // the match. Arrow keys keep their full "ArrowUp" name.
      const key = e.key.length === 1 ? e.key.toLowerCase() : e.key;
      const next = [...konamiBufferRef.current, key].slice(
        -KONAMI_SEQUENCE.length,
      );
      konamiBufferRef.current = next;
      if (
        next.length === KONAMI_SEQUENCE.length &&
        KONAMI_SEQUENCE.every((k, i) => k === next[i])
      ) {
        setChangeStoreUnlocked(true);
        konamiBufferRef.current = [];
      }
    };

    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [changeStoreUnlocked]);

  const form = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      userName: "",
      password: "",
    },
  });

  function handleSubmit(values: LoginFormValues) {
    onSubmit(values.userName, values.password);
  }

  return (
    <div className="w-full">
      <div className="mb-6 space-y-1">
        {storeLabel ? (
          <>
            <p className="text-base text-muted-foreground">Welcome back to</p>
            <h2 className="text-3xl font-bold tracking-tight">{storeLabel}</h2>
          </>
        ) : (
          <h2 className="text-3xl font-bold tracking-tight">Welcome back</h2>
        )}
        <p className="text-base text-muted-foreground">
          Enter your credentials to continue
        </p>
      </div>

      <form onSubmit={form.handleSubmit(handleSubmit)}>
        <FieldGroup>
          <Controller
            name="userName"
            control={form.control}
            render={({ field, fieldState }) => (
              <Field data-invalid={fieldState.invalid}>
                <FieldLabel>Username</FieldLabel>
                <Input
                  {...field}
                  type="text"
                  placeholder="Enter your username"
                  aria-invalid={fieldState.invalid}
                  disabled={isLoading}
                  autoComplete="username"
                />
                {fieldState.invalid && (
                  <FieldError errors={[fieldState.error]} />
                )}
              </Field>
            )}
          />

          <Controller
            name="password"
            control={form.control}
            render={({ field, fieldState }) => (
              <Field data-invalid={fieldState.invalid}>
                <FieldLabel>Password</FieldLabel>
                <InputGroup>
                  <InputGroupInput
                    {...field}
                    type={showPassword ? "text" : "password"}
                    placeholder="Enter your password"
                    aria-invalid={fieldState.invalid}
                    disabled={isLoading}
                    autoComplete="current-password"
                  />
                  <InputGroupAddon align="inline-end">
                    <InputGroupButton
                      size="icon-xs"
                      onClick={() => setShowPassword((prev) => !prev)}
                      tabIndex={-1}
                      aria-label={
                        showPassword ? "Hide password" : "Show password"
                      }
                    >
                      {showPassword ? (
                        <EyeOff className="size-4 text-muted-foreground" />
                      ) : (
                        <Eye className="size-4 text-muted-foreground" />
                      )}
                    </InputGroupButton>
                  </InputGroupAddon>
                </InputGroup>
                {fieldState.invalid && (
                  <FieldError errors={[fieldState.error]} />
                )}
              </Field>
            )}
          />

          {error && (
            <div className="rounded-md bg-destructive/10 px-3 py-2 text-base text-destructive">
              {error}
            </div>
          )}

          {deviceConflictError && (
            <div className="rounded-md bg-destructive/10 px-3 py-2 text-base text-destructive">
              <p>{deviceConflictError}</p>
              {onForceLogin && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="mt-2"
                  onClick={onForceLogin}
                  disabled={isLoading}
                >
                  Force Login
                </Button>
              )}
            </div>
          )}

          <Button
            type="submit"
            size="lg"
            className="w-full"
            disabled={isLoading}
          >
            {isLoading ? "Signing in..." : "Sign In →"}
          </Button>

          {changeStoreUnlocked && (
            <button
              type="button"
              onClick={onChangeDevice}
              className="w-full text-center text-sm text-muted-foreground hover:text-foreground"
            >
              Change Store
            </button>
          )}
        </FieldGroup>
      </form>
    </div>
  );
}
